import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { SelectChangeEvent } from 'typings/events/selection-change-event';

import { Heatmap } from 'src/components/charts/Heatmap';
import { SeriesControls } from 'src/components/charts/helpers/SeriesControls';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import {
  Resolution,
  UI__ServiceReportQuery_service_stats_queryStats,
} from 'src/lib/graphqlTypes/types';

import { PercentileChart } from './PercentileChart';
import { RequestLatencyOverTimeHeading } from './RequestLatencyOverTimeHeading';
import { useServiceReportHeatmaps } from './ServiceReportHeatmaps';
import { LatencyChartType } from './types';

const getChartDescription = ({
  chartType,
  isSubscriptionOperation,
}: {
  chartType: LatencyChartType;
  isSubscriptionOperation: boolean;
}) => {
  if (chartType === 'heatmap') {
    return isSubscriptionOperation
      ? 'The heatmap of registration latency distributions over time.'
      : 'The heatmap of latency distributions over time.';
  } else if (chartType === 'p50') {
    return isSubscriptionOperation
      ? 'The 50th percentile of subscription registration latency over time.'
      : 'The 50th percentile of request latency over time.';
  } else if (chartType === 'p90') {
    return isSubscriptionOperation
      ? 'The 90th percentile of subscription registration latency over time.'
      : 'The 90th percentile of request latency over time.';
  } else if (chartType === 'p95') {
    return isSubscriptionOperation
      ? 'The 95th percentile of subscription registration latency over time.'
      : 'The 95th percentile of request latency over time.';
  }

  assertUnreachable(chartType);
};

interface RequestLatencyOverTimeProps {
  isSubscriptionOperation: boolean;
  requestsHeatmapStackInfo: ReturnType<
    typeof useServiceReportHeatmaps
  >['requestsHeatmapStackInfo'];
  requestsChartData: UI__ServiceReportQuery_service_stats_queryStats[];
  resolution: Resolution;
}

export function RequestLatencyOverTime({
  isSubscriptionOperation,
  requestsHeatmapStackInfo,
  requestsChartData,
  resolution,
}: RequestLatencyOverTimeProps) {
  const [chartType, setChartType] = useState<LatencyChartType>('heatmap');

  return (
    <Card variant="unstyled">
      <CardHeader>
        <CardTitle>
          <RequestLatencyOverTimeHeading
            chartType={chartType}
            onChartTypeChange={(event: SelectChangeEvent<LatencyChartType>) => {
              setChartType(event.target.value);
            }}
            isSubscriptionOperation={isSubscriptionOperation}
          />
        </CardTitle>
        <CardDescription className="pl-3">
          {getChartDescription({ chartType, isSubscriptionOperation })}
        </CardDescription>
      </CardHeader>
      <CardBody>
        {chartType === 'heatmap' ? (
          <>
            <Heatmap
              {...requestsHeatmapStackInfo}
              resolution={resolution}
              className="h-36"
              data-testid="requests-heatmap"
            />
            <SeriesControls
              className="w-full px-4 pt-6 text-center"
              hideWhenSingleStack
              {...requestsHeatmapStackInfo}
            />
          </>
        ) : (
          <PercentileChart
            requestsChartData={requestsChartData}
            chartType={chartType}
            resolution={resolution}
          />
        )}
      </CardBody>
    </Card>
  );
}
